/* ---------------------------------------------------------------------------
   The roads and the water around a structure, read out of this deployment's own
   base map for the 3D ground (./ground3d.ts).

   The same vector tiles the 2D map draws — GET /api/basemap/:name/:z/:x/:y, the
   OpenMapTiles schema — decoded here with @mapbox/vector-tile, and only the
   three layers the location map draws besides contours: `transportation` (roads,
   not rail or paths), `waterway` (rivers and streams as lines) and `water`
   (rivers wide enough to have banks, as their outlines). Returned as lines of
   [lng, lat], to be laid on the ground by whoever has its heights.

   THE ZOOM IS THE CIRCLE'S: z14, the archive's deepest, for a circle up to 3 km;
   z13 beyond, so a 10 km circle is a few dozen tiles rather than a hundred and
   fifty. Simplified a little at z13, which at that size nobody can see.
   --------------------------------------------------------------------------- */

import { VectorTile } from '@mapbox/vector-tile';
import Pbf from 'pbf';
import { circleBounds } from './isolate.ts';

export type FeatureKind = 'major' | 'minor' | 'river' | 'stream' | 'water';

export interface SiteLine {
  kind: FeatureKind;
  /** [lng, lat] pairs. */
  coords: [number, number][];
  /** OSM's `brunnel=bridge`: carried over whatever is under it. */
  bridge: boolean;
}

const MAJOR = new Set(['motorway', 'trunk', 'primary', 'secondary', 'tertiary']);
const MINOR = new Set(['minor', 'service', 'track']);
const STREAMS = new Set(['stream', 'canal', 'drain', 'ditch']);

function tileOf(lng: number, lat: number, z: number): { x: number; y: number } {
  const n = 2 ** z;
  const rad = (lat * Math.PI) / 180;
  return {
    x: Math.floor(((lng + 180) / 360) * n),
    y: Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n),
  };
}

/** A point in tile `x/y/z`'s own coordinates, as [lng, lat]. */
function toLngLat(x: number, y: number, z: number, extent: number, px: number, py: number): [number, number] {
  const n = 2 ** z;
  const wx = (x + px / extent) / n;
  const wy = (y + py / extent) / n;
  const lng = wx * 360 - 180;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - 2 * wy))) * 180) / Math.PI;
  return [lng, lat];
}

/** A mapped water area, whole: its rings of [lng, lat], read even-odd. For telling where water is, not for drawing. */
export interface SiteArea {
  rings: [number, number][][];
}

/**
 * A line cut to its tile's own square, [0, extent] each way, as runs of tile
 * coordinates. A tile carries its lines a little past its edge, so without
 * this every line is drawn twice where two tiles meet — and a river's water
 * level, worked out along each piece, steps at the seam.
 */
function clipToTile(ring: readonly { x: number; y: number }[], e: number): [number, number][][] {
  const runs: [number, number][][] = [];
  let run: [number, number][] = [];
  const flush = () => {
    if (run.length > 1) runs.push(run);
    run = [];
  };
  for (let k = 1; k < ring.length; k++) {
    const a = ring[k - 1]!;
    const b = ring[k]!;
    /* Liang–Barsky against the square. */
    let t0 = 0;
    let t1 = 1;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    let ok = true;
    for (const [p, q] of [[-dx, a.x], [dx, e - a.x], [-dy, a.y], [dy, e - a.y]] as const) {
      if (p === 0) {
        if (q < 0) ok = false;
        continue;
      }
      const r = q / p;
      if (p < 0) t0 = Math.max(t0, r);
      else t1 = Math.min(t1, r);
    }
    if (!ok || t0 > t1) {
      flush();
      continue;
    }
    if (t0 > 0) flush();
    if (run.length === 0) run.push([a.x + dx * t0, a.y + dy * t0]);
    run.push([a.x + dx * t1, a.y + dy * t1]);
    if (t1 < 1) flush();
  }
  flush();
  return runs;
}

/**
 * Lines cut at the tiles' edges joined back up: wherever one line of a kind
 * ends within a couple of metres of where another of the same kind starts, the
 * two become one — so a river runs as one course, its direction kept, across
 * every tile it crosses.
 */
function joinLines(lines: SiteLine[]): SiteLine[] {
  const TOL = 2e-5; // degrees, about 2 m
  const cell = (p: [number, number]) => [Math.round(p[0] / TOL), Math.round(p[1] / TOL)] as const;
  const keyOf = (cx: number, cy: number) => `${cx},${cy}`;
  const group = (l: SiteLine) => `${l.kind}|${l.bridge}`;
  const byStart = new Map<string, number[]>();
  const hasBefore = new Set<number>();
  lines.forEach((l, i) => {
    const [cx, cy] = cell(l.coords[0]!);
    const k = keyOf(cx, cy);
    (byStart.get(k) ?? byStart.set(k, []).get(k)!).push(i);
  });
  const next = (i: number, used: Set<number>): number => {
    const l = lines[i]!;
    const end = l.coords[l.coords.length - 1]!;
    const [cx, cy] = cell(end);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const j of byStart.get(keyOf(cx + dx, cy + dy)) ?? []) {
          if (j === i || used.has(j) || group(lines[j]!) !== group(l)) continue;
          const s = lines[j]!.coords[0]!;
          if (Math.abs(s[0] - end[0]) <= TOL && Math.abs(s[1] - end[1]) <= TOL) return j;
        }
      }
    }
    return -1;
  };
  lines.forEach((_, i) => {
    const j = next(i, new Set());
    if (j >= 0) hasBefore.add(j);
  });
  const used = new Set<number>();
  const out: SiteLine[] = [];
  /* From every line nothing runs into first, then whatever is left (closed loops). */
  const order = [...lines.keys()].sort((a, b) => Number(hasBefore.has(a)) - Number(hasBefore.has(b)));
  for (const i of order) {
    if (used.has(i)) continue;
    used.add(i);
    const coords = [...lines[i]!.coords];
    for (let j = next(i, used); j >= 0; j = next(j, used)) {
      used.add(j);
      coords.push(...lines[j]!.coords.slice(1));
    }
    out.push({ ...lines[i]!, coords });
  }
  return out;
}

/** Every road and water line inside the tiles that cover a circle. */
export async function loadSiteLines(
  region: string,
  at: { lat: number; lng: number },
  radius: number,
): Promise<SiteLine[]> {
  return (await loadSite(region, at, radius)).lines;
}

/** The same, with the water areas whole besides — one tile's piece of an area each, which together cover it. */
export async function loadSite(
  region: string,
  at: { lat: number; lng: number },
  radius: number,
): Promise<{ lines: SiteLine[]; areas: SiteArea[] }> {
  const z = radius <= 3000 ? 14 : 13;
  const [[west, south], [east, north]] = circleBounds(at.lat, at.lng, radius);
  const nw = tileOf(west, north, z);
  const se = tileOf(east, south, z);

  const jobs: Promise<{ lines: SiteLine[]; areas: SiteArea[] }>[] = [];
  for (let x = nw.x; x <= se.x; x++) {
    for (let y = nw.y; y <= se.y; y++) {
      jobs.push(
        (async () => {
          const res = await fetch(`/api/basemap/${region}/${z}/${x}/${y}`).catch(() => null);
          if (!res || res.status !== 200) return { lines: [], areas: [] };
          const tile = new VectorTile(new Pbf(new Uint8Array(await res.arrayBuffer())));
          const out: SiteLine[] = [];
          const areas: SiteArea[] = [];
          const take = (layerName: string, kindOf: (props: Record<string, unknown>) => FeatureKind | null) => {
            const layer = tile.layers[layerName];
            if (!layer) return;
            for (let i = 0; i < layer.length; i++) {
              const f = layer.feature(i);
              const kind = kindOf(f.properties);
              if (!kind || (f.type !== 2 && f.type !== 3)) continue;
              if (f.properties['brunnel'] === 'tunnel') continue;
              const bridge = f.properties['brunnel'] === 'bridge';
              const e = layer.extent;
              if (layerName === 'water' && f.type === 3) {
                areas.push({ rings: f.loadGeometry().map((ring) => ring.map((p) => toLngLat(x, y, z, e, p.x, p.y))) });
              }
              /* A polygon is cut at the tile's edge (and its buffer), and its outline
                 would draw that cut as a straight line across the ground. So an
                 outline is split into runs, leaving out every step that lies along
                 or beyond an edge — what is left is the bank. */
              const onEdge = (a: { x: number; y: number }, b: { x: number; y: number }): boolean =>
                (a.x <= 0 && b.x <= 0) || (a.x >= e && b.x >= e) || (a.y <= 0 && b.y <= 0) || (a.y >= e && b.y >= e);
              for (const ring of f.loadGeometry()) {
                if (ring.length < 2) continue;
                if (f.type === 2) {
                  for (const run of clipToTile(ring, e)) out.push({ kind, bridge, coords: run.map(([px, py]) => toLngLat(x, y, z, e, px, py)) });
                  continue;
                }
                let run: [number, number][] = [];
                for (let k = 1; k < ring.length; k++) {
                  const a = ring[k - 1]!;
                  const b = ring[k]!;
                  if (onEdge(a, b)) {
                    if (run.length > 1) out.push({ kind, bridge, coords: run });
                    run = [];
                    continue;
                  }
                  if (run.length === 0) run.push(toLngLat(x, y, z, e, a.x, a.y));
                  run.push(toLngLat(x, y, z, e, b.x, b.y));
                }
                if (run.length > 1) out.push({ kind, bridge, coords: run });
              }
            }
          };
          take('transportation', (p) => {
            const c = String(p['class'] ?? '');
            return MAJOR.has(c) ? 'major' : MINOR.has(c) ? 'minor' : null;
          });
          take('waterway', (p) => {
            const c = String(p['class'] ?? '');
            return c === 'river' ? 'river' : STREAMS.has(c) ? 'stream' : null;
          });
          take('water', () => 'water');
          return { lines: out, areas };
        })(),
      );
    }
  }
  const tiles = await Promise.all(jobs);
  return { lines: joinLines(tiles.flatMap((t) => t.lines)), areas: tiles.flatMap((t) => t.areas) };
}
